/**
 * Operations View Component
 *
 * Displays table operations history using DataTable for consistency.
 * Shows operation type, status, summary, timing, and duration.
 *
 * Features:
 * - DataTable with sortable columns
 * - Filter by operation category (All, Uploads, Schema Changes)
 * - Expandable row details
 * - Real-time polling for active operations
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  History,
} from 'lucide-react';
import { Badge, cn } from '@loader/ui';
import {
  TABLE_OPERATION_TYPE_LABELS,
  TABLE_OPERATION_STATUS_LABELS,
  isActiveOperationStatus,
  formatOperationSummary,
  type TableOperation,
} from '@loader/shared-types';

import { DataTable, createTextColumn, createDateColumn, type DataGridSortDirection, type DataGridFilter } from '@/components/data-grid';
import { useTableOperationsListQuery, useTableVersionsQuery } from '@/features/tables/hooks/useTableQueries';
import { formatDateTimeShort, getOperationDuration } from '@/lib/formatters';
import { EmptyView, getErrorCountLabel } from '@/components/common';
import { OperationLogsDialog } from '@/features/tables/components/dialogs';

// ============================================================================
// TYPES
// ============================================================================

interface OperationsViewProps {
  tableId: string;
  variant?: 'default' | 'flush';
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getStatusIcon(status: TableOperation['status'], className = 'h-3 w-3') {
  switch (status) {
    case 'pending':
      return <RefreshCw className={className} />;
    case 'in_progress':
      return <Loader2 className={cn(className, 'animate-spin')} />;
    case 'completed':
      return <CheckCircle2 className={className} />;
    case 'failed':
      return <XCircle className={className} />;
  }
}

function getStatusBadgeVariant(
  status: TableOperation['status']
): 'success' | 'destructive' | 'info' | 'muted' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'destructive';
    case 'in_progress':
      return 'info';
    case 'pending':
      return 'muted';
  }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function OperationsView({ tableId, variant = 'default' }: OperationsViewProps) {
  // Pagination state
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Sorting state
  const [sortBy, setSortBy] = useState<string | undefined>('started_at');
  const [sortDir, setSortDir] = useState<DataGridSortDirection>('desc');

  // Filtering state
  const [filters, setFilters] = useState<DataGridFilter[]>([]);

  // Track if we should poll for active operations
  const [shouldPoll, setShouldPoll] = useState(false);

  // Dialog state for operation logs
  const [selectedOperation, setSelectedOperation] = useState<TableOperation | null>(null);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);

  // Fetch operations with server-side pagination and dynamic polling
  const {
    data: operationsResponse,
    isLoading,
    error,
  } = useTableOperationsListQuery(tableId, {
    limit: pageSize,
    offset: pageIndex * pageSize,
    enabled: !!tableId,
    refetchInterval: shouldPoll ? 3000 : false,
  });

  // Extract operations and total from response
  const operations = operationsResponse?.operations;
  const serverTotal = operationsResponse?.pagination?.total ?? 0;

  // Fetch versions (typically already cached by preview panel) for error count labels
  const { data: versions } = useTableVersionsQuery(tableId, { enabled: !!tableId });

  // Build version lookup by ID for fast access in column renderers
  const versionById = useMemo(() => {
    if (!versions) return new Map<string, { error_message?: string | null; error_details?: Record<string, unknown> | null }>();
    return new Map(versions.map((v) => [v.id, v]));
  }, [versions]);

  // Update polling state when data changes
  const hasActiveOperations = useMemo(() => {
    if (!operations) return false;
    return operations.some((op: TableOperation) => isActiveOperationStatus(op.status));
  }, [operations]);

  // Sync polling state with useEffect to avoid render loop
  useEffect(() => {
    setShouldPoll(hasActiveOperations);
  }, [hasActiveOperations]);

  // Sorting and filtering callbacks
  const handleSortChange = useCallback((newSortBy: string | undefined, newSortDir?: DataGridSortDirection) => {
    setSortBy(newSortBy);
    setSortDir(newSortDir ?? 'asc');
    setPageIndex(0);
  }, []);

  const handleFilterChange = useCallback((filter: DataGridFilter) => {
    setFilters(prev => {
      const existing = prev.findIndex(f => f.column_id === filter.column_id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = filter;
        return updated;
      }
      return [...prev, filter];
    });
    setPageIndex(0);
  }, []);

  const handleFilterRemove = useCallback((columnId: string) => {
    setFilters(prev => prev.filter(f => f.column_id !== columnId));
    setPageIndex(0);
  }, []);

  const handleFiltersClear = useCallback(() => {
    setFilters([]);
    setPageIndex(0);
  }, []);

  // Filtered data
  const filteredData = useMemo(() => {
    if (!operations) return [];

    return operations.filter((op: TableOperation) => {
      return filters.every(filter => {
        const value = filter.value?.toString().toLowerCase() || '';
        if (!value) return true;

        switch (filter.column_id) {
          case 'operation_type':
            return TABLE_OPERATION_TYPE_LABELS[op.operation_type].toLowerCase().includes(value);
          case 'status':
            return TABLE_OPERATION_STATUS_LABELS[op.status].toLowerCase().includes(value);
          case 'params':
            return formatOperationSummary(op.operation_type, op.params).toLowerCase().includes(value);
          default:
            return true;
        }
      });
    });
  }, [operations, filters]);

  // Sorted data
  const sortedData = useMemo(() => {
    if (!sortBy) return filteredData;

    return [...filteredData].sort((a, b) => {
      let aVal: unknown;
      let bVal: unknown;

      switch (sortBy) {
        case 'operation_type':
          aVal = TABLE_OPERATION_TYPE_LABELS[a.operation_type];
          bVal = TABLE_OPERATION_TYPE_LABELS[b.operation_type];
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        case 'started_at':
          aVal = a.started_at ? new Date(a.started_at).getTime() : 0;
          bVal = b.started_at ? new Date(b.started_at).getTime() : 0;
          break;
        case 'completed_at':
          aVal = a.completed_at ? new Date(a.completed_at).getTime() : 0;
          bVal = b.completed_at ? new Date(b.completed_at).getTime() : 0;
          break;
        default:
          aVal = (a as unknown as Record<string, unknown>)[sortBy];
          bVal = (b as unknown as Record<string, unknown>)[sortBy];
      }

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDir === 'desc' ? -comparison : comparison;
    });
  }, [filteredData, sortBy, sortDir]);

  // Data for display - with client-side filtering, use filtered count; otherwise use server total
  const displayData = sortedData;
  const totalItems = filters.length > 0 ? sortedData.length : serverTotal;
  const pageCount = Math.ceil(totalItems / pageSize);

  // Column definitions
  const columns = useMemo((): ColumnDef<TableOperation>[] => {
    return [
      createTextColumn<TableOperation>('operation_type', 'Type', {
        minWidth: 140,
        maxWidth: 180,
        cellRenderer: (_value, original) => {
          const op = original as TableOperation;
          return (
            <span className="truncate">{TABLE_OPERATION_TYPE_LABELS[op.operation_type]}</span>
          );
        },
      }),
      createTextColumn<TableOperation>('status', 'Status', {
        minWidth: 120,
        maxWidth: 180,
        cellRenderer: (_value, original) => {
          const op = original as TableOperation;

          // For failed version_uploaded operations, show error count from version data
          let statusLabel = TABLE_OPERATION_STATUS_LABELS[op.status];
          if (op.status === 'failed' && op.operation_type === 'version_uploaded' && op.version_id) {
            const version = versionById.get(op.version_id);
            if (version?.error_message) {
              statusLabel = getErrorCountLabel(
                version.error_message,
                version.error_details,
              );
            }
          }

          return (
            <Badge variant={getStatusBadgeVariant(op.status)} className="gap-1.5 rounded-full">
              {getStatusIcon(op.status, 'h-3 w-3')}
              {statusLabel}
            </Badge>
          );
        },
      }),
      createTextColumn<TableOperation>('params', 'Summary', {
        minWidth: 200,
        maxWidth: 300,
        cellRenderer: (_value, original) => {
          const op = original as TableOperation;
          const summary = formatOperationSummary(op.operation_type, op.params);
          return (
            <span className="text-muted-foreground truncate" title={summary}>
              {summary}
            </span>
          );
        },
      }),
      createDateColumn<TableOperation>('started_at', 'Started', {
        minWidth: 160,
        maxWidth: 200,
        cellRenderer: (value) => {
          if (!value) return <span className="text-muted-foreground">-</span>;
          return (
            <span className="text-muted-foreground">
              {formatDateTimeShort(value as string)}
            </span>
          );
        },
      }),
      createTextColumn<TableOperation>('completed_at', 'Duration', {
        minWidth: 100,
        maxWidth: 120,
        cellRenderer: (_value, original) => {
          const op = original as TableOperation;
          const duration = getOperationDuration(op.started_at, op.completed_at);
          return <span className="text-muted-foreground font-mono text-xs">{duration}</span>;
        },
      }),
    ];
  }, [versionById]);

  // Handle row click - open error dialog for failed operations only
  const handleRowClick = useCallback((operation: TableOperation) => {
    if (operation.status !== 'failed') return;
    setSelectedOperation(operation);
    setLogsDialogOpen(true);
  }, []);

  const containerClass = variant === 'flush' ? '' : 'px-4';

  // Error state
  if (error) {
    return (
      <div className={cn('py-4', containerClass)}>
        <EmptyView
          title="Error loading operations"
          description={error.message || 'Failed to load operations history'}
          icon={History}
          className="h-64"
        />
      </div>
    );
  }

  // Empty state
  if (!isLoading && (!operations || operations.length === 0)) {
    return (
      <div className={cn('py-4', containerClass)}>
        <EmptyView
          title="No operations yet"
          description="Operations will appear here when you upload versions, change field types, or modify constraints."
          icon={History}
          className="h-64"
        />
      </div>
    );
  }

  return (
    <>
      <div className={cn('flex flex-col h-full min-h-0', containerClass)}>
        <DataTable<TableOperation>
          columns={columns}
          data={displayData}
          isLoading={isLoading}
          pageIndex={pageIndex}
          pageSize={pageSize}
          totalItems={totalItems}
          pageCount={pageCount}
          onPageChange={setPageIndex}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPageIndex(0);
          }}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={handleSortChange}
          filters={filters}
          onFilterChange={handleFilterChange}
          onFilterRemove={handleFilterRemove}
          onFiltersClear={handleFiltersClear}
          enablePagination
          enableFiltering
          hideVerticalLines
          variant="flush"
          onRowClick={handleRowClick}
          emptyStateComponent={
            <EmptyView
              title="No operations found"
              description="No operations match the current filter."
              icon={History}
              className="h-48"
            />
          }
        />
      </div>

      <OperationLogsDialog
        open={logsDialogOpen}
        onOpenChange={setLogsDialogOpen}
        tableId={tableId}
        operation={selectedOperation}
      />
    </>
  );
}
